import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
    ShieldCheck, Search, MapPin, X, Clock, ArrowRight, AlertCircle,
    Home, HeartPulse, Activity, Car, Home as Building, Plane, Briefcase, Phone, ChevronDown, Info
} from 'lucide-react';
import { FaWhatsapp, FaPhone, FaFacebookF, FaInstagram, FaTwitter, FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import './Navbar.css';

const NAV_LINKS = [
    { key: 'nav_home',      path: '/', icon: Home },
    { key: 'nav_insurance', path: '#', icon: ShieldCheck, hasMega: true },
    { key: 'nav_about',     path: '/about', icon: Info },
    { key: 'nav_contact',   path: '/contact', icon: Phone },
];

const MEGA_MENU = [
    {
        title: 'ft_life',
        icon: HeartPulse,
        links: [
            { key: 'ft_life_term', path: '/insurance/term-insurance' },
            { key: 'ft_life_whole', path: '/insurance/whole-life-insurance' },
            { key: 'ft_life_endow', path: '/insurance/endowment-plan' },
            { key: 'ft_life_money', path: '/insurance/money-back-plan' },
            { key: 'ft_life_ulip', path: '/insurance/ulip' },
            { key: 'ft_life_pension', path: '/insurance/pension-plan' },
        ]
    },
    {
        title: 'ft_health',
        icon: Activity,
        links: [
            { key: 'ft_health_indiv', path: '/insurance/individual-health' },
            { key: 'ft_health_family', path: '/insurance/family-floater' },
            { key: 'ft_health_senior', path: '/insurance/senior-citizen-health' },
            { key: 'ft_health_critical', path: '/insurance/critical-illness' },
            { key: 'ft_health_group', path: '/insurance/group-health' },
            { key: 'ft_health_accident', path: '/insurance/personal-accident' },
        ]
    },
    {
        title: 'ft_motor',
        icon: Car,
        links: [
            { key: 'ft_motor_car', path: '/insurance/car-insurance' },
            { key: 'ft_motor_bike', path: '/insurance/two-wheeler-insurance' },
            { key: 'ft_motor_comm', path: '/insurance/commercial-vehicle' },
            { key: 'ft_motor_third', path: '/insurance/third-party-motor' },
            { key: 'ft_motor_compre', path: '/insurance/comprehensive-motor' },
        ]
    },
    {
        title: 'ft_property',
        icon: Building,
        links: [
            { key: 'ft_prop_home', path: '/insurance/home-insurance' },
            { key: 'ft_prop_fire', path: '/insurance/fire-insurance' },
            { key: 'ft_prop_burg', path: '/insurance/burglary-insurance' },
            { key: 'ft_prop_shop', path: '/insurance/shop-insurance' },
            { key: 'ft_prop_indus', path: '/insurance/industrial-insurance' },
        ]
    },
    {
        title: 'ft_business',
        icon: Briefcase,
        links: [
            { key: 'ft_biz_liab', path: '/insurance/liability-insurance' },
            { key: 'ft_biz_indem', path: '/insurance/professional-indemnity' },
            { key: 'ft_biz_cyber', path: '/insurance/cyber-insurance' },
            { key: 'ft_biz_marine', path: '/insurance/marine-insurance' },
            { key: 'ft_biz_comp', path: '/insurance/workmen-compensation' },
        ]
    },
    {
        title: 'ft_travel',
        icon: Plane,
        links: [
            { key: 'ft_travel_dom', path: '/insurance/domestic-travel' },
            { key: 'ft_travel_intl', path: '/insurance/international-travel' },
            { key: 'ft_travel_stud', path: '/insurance/student-travel' },
            { key: 'ft_travel_fam', path: '/insurance/family-travel' },
        ]
    },
    {
        title: 'ft_others',
        icon: ShieldCheck,
        links: [
            { key: 'ft_oth_mobile', path: '/insurance/mobile-insurance' },
            { key: 'ft_oth_gadget', path: '/insurance/gadget-insurance' },
            { key: 'ft_oth_pet', path: '/insurance/pet-insurance' },
            { key: 'ft_oth_crop', path: '/insurance/crop-insurance' },
            { key: 'ft_oth_event', path: '/insurance/event-insurance' },
            { key: 'ft_oth_wed', path: '/insurance/wedding-insurance' },
        ]
    }
];

const RECOMMENDATIONS = [
    // Life
    { title: 'Term Insurance', path: '/insurance/term-insurance', category: 'Life' },
    { title: 'Whole Life Insurance', path: '/insurance/whole-life-insurance', category: 'Life' },
    { title: 'Endowment Plan', path: '/insurance/endowment-plan', category: 'Life' },
    { title: 'Money Back Plan', path: '/insurance/money-back-plan', category: 'Life' },
    { title: 'ULIP Plan', path: '/insurance/ulip', category: 'Life' },
    { title: 'Pension Plan', path: '/insurance/pension-plan', category: 'Life' },
    
    // Health
    { title: 'Individual Health', path: '/insurance/individual-health', category: 'Health' },
    { title: 'Family Floater', path: '/insurance/family-floater', category: 'Health' },
    { title: 'Critical Illness', path: '/insurance/critical-illness', category: 'Health' },
    { title: 'Senior Citizen Health', path: '/insurance/senior-citizen-health', category: 'Health' },
    { title: 'Group Health Insurance', path: '/insurance/group-health', category: 'Health' },
    { title: 'Personal Accident', path: '/insurance/personal-accident', category: 'Health' },
    
    // Motor
    { title: 'Car Insurance', path: '/insurance/car-insurance', category: 'Motor' },
    { title: 'Two-Wheeler Insurance', path: '/insurance/two-wheeler-insurance', category: 'Motor' },
    { title: 'Commercial Vehicle', path: '/insurance/commercial-vehicle', category: 'Motor' },
    { title: 'Third-Party Insurance', path: '/insurance/third-party-motor', category: 'Motor' },
    { title: 'Comprehensive Motor', path: '/insurance/comprehensive-motor', category: 'Motor' },
    
    // Property
    { title: 'Home Insurance', path: '/insurance/home-insurance', category: 'Property' },
    { title: 'Fire Insurance', path: '/insurance/fire-insurance', category: 'Property' },
    { title: 'Burglary Insurance', path: '/insurance/burglary-insurance', category: 'Property' },
    { title: 'Shop Insurance', path: '/insurance/shop-insurance', category: 'Property' },
    { title: 'Industrial Insurance', path: '/insurance/industrial-insurance', category: 'Property' },
    
    // Business
    { title: 'Liability Insurance', path: '/insurance/liability-insurance', category: 'Business' },
    { title: 'Professional Indemnity', path: '/insurance/professional-indemnity', category: 'Business' },
    { title: 'Cyber Insurance', path: '/insurance/cyber-insurance', category: 'Business' },
    { title: 'Marine Insurance', path: '/insurance/marine-insurance', category: 'Business' },
    { title: 'Workmen Compensation', path: '/insurance/workmen-compensation', category: 'Business' },
    
    // Travel
    { title: 'Domestic Travel', path: '/insurance/domestic-travel', category: 'Travel' },
    { title: 'International Travel', path: '/insurance/international-travel', category: 'Travel' },
    { title: 'Student Travel', path: '/insurance/student-travel', category: 'Travel' },
    { title: 'Family Travel', path: '/insurance/family-travel', category: 'Travel' },
    
    // Others
    { title: 'Mobile Insurance', path: '/insurance/mobile-insurance', category: 'Others' },
    { title: 'Gadget Insurance', path: '/insurance/gadget-insurance', category: 'Others' },
    { title: 'Pet Insurance', path: '/insurance/pet-insurance', category: 'Others' },
    { title: 'Crop Insurance', path: '/insurance/crop-insurance', category: 'Others' },
    { title: 'Event Insurance', path: '/insurance/event-insurance', category: 'Others' },
    { title: 'Wedding Insurance', path: '/insurance/wedding-insurance', category: 'Others' },
    
    // Actions
    { title: 'Contact Support', path: '/contact', category: 'Support' },
    { title: 'Find Branch', path: '/contact', category: 'Support' },
    { title: 'Submit Claim', path: '/contact', category: 'Support' },
];

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen]   = useState(false);
    const [scrolled, setScrolled]       = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [history, setHistory]         = useState(['Life Insurance', 'Health Benefits']);
    const [showMega, setShowMega]       = useState(false);

    const { t, lang, setLanguage } = useLanguage();
    const location   = useLocation();
    const navigate   = useNavigate();
    const navRef     = useRef(null);
    
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setShowResults(false);
                setShowMega(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setShowResults(e.target.value.length > 0);
        setShowMega(false);
    };

    const addToHistory = (q) => {
        if (!q.trim()) return;
        setHistory(prev => [q, ...prev.filter(i => i !== q)].slice(0, 5));
    };

    const closeSearch = () => {
        setShowResults(false);
        setSearchQuery('');
    };

    const handleSearchSubmit = (e) => {
        if (e.key === 'Enter' && searchQuery.trim()) {
            const match = RECOMMENDATIONS.find(r => 
                r.title.toLowerCase().includes(searchQuery.toLowerCase())
            );
            if (match) {
                addToHistory(match.title);
                navigate(match.path);
            } else {
                addToHistory(searchQuery);
            }
            closeSearch();
        }
    };

    const selectResult = (path, title) => {
        addToHistory(title);
        navigate(path);
        closeSearch();
    };

    const filteredRecs = RECOMMENDATIONS.filter(r => 
        r.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), []);
    const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

    return (
        <header ref={navRef} className="navbar-wrapper">
            {/* TOP BAR */}
            <div className={`top-bar${scrolled ? ' top-bar--hidden' : ''}`}>
                <div className="container top-bar-content">
                    <div className="top-bar-left">
                        <a href="#" className="social-link facebook"><FaFacebookF /></a>
                        <a href="#" className="social-link instagram"><FaInstagram /></a>
                        <a href="#" className="social-link twitter"><FaTwitter /></a>
                        <a href="#" className="social-link linkedin"><FaLinkedinIn /></a>
                        <a href="#" className="social-link youtube"><FaYoutube /></a>
                    </div>
                    <div className="top-bar-right">
                        <a href="https://wa.me/919667193590" className="top-bar-item whatsapp-link"><FaWhatsapp size={19} /><span>{t('nav_whatsapp')}</span></a>
                        <span className="top-bar-divider" />
                        <a href="tel:+919667193590" className="top-bar-item phone-link"><FaPhone style={{ transform: 'scaleX(-1)' }} /><span>{t('nav_phone')}</span></a>
                        <span className="top-bar-divider" />
                        <a href="#" className="top-bar-item location-link"><MapPin /><span>{t('nav_branch')}</span></a>
                        <span className="top-bar-divider" />
                        <div className="lang-switcher">
                            <button className={`lang-btn${lang === 'en' ? ' lang-active' : ''}`} onClick={() => setLanguage('en')}>EN</button>
                            <span className="lang-sep">|</span>
                            <button className={`lang-btn${lang === 'hi' ? ' lang-active' : ''}`} onClick={() => setLanguage('hi')}>हिं</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN NAVBAR */}
            <div className={`navbar${scrolled ? ' scrolled' : ''}`}>
                <div className="nav-content container">
                    <Link to="/" className="logo">
                        <img src="/assets/logo.png" alt="Bharatinsurex" className="navbar-logo-img" />
                    </Link>

                    <div className="nav-right-stack">
                        <nav className="desktop-links">
                            <ul className="nav-list">
                                {NAV_LINKS.map(link => (
                                    <li 
                                        key={link.key} 
                                        onMouseEnter={() => link.hasMega ? setShowMega(true) : setShowMega(false)}
                                        className={link.hasMega ? 'has-mega' : ''}
                                    >
                                        <Link 
                                            to={link.path} 
                                            className={isActive(link.path) && !link.hasMega ? 'active' : ''}
                                            onClick={() => setShowMega(false)}
                                        >
                                            <link.icon size={16} className="nav-icon" />
                                            <span>{t(link.key)}</span>
                                            {link.hasMega && <ChevronDown size={14} className={`chevron ${showMega ? 'rotate' : ''}`} />}
                                        </Link>

                                        {link.hasMega && showMega && (
                                            <div className="mega-menu-dropdown" onMouseLeave={() => setShowMega(false)}>
                                                <div className="mega-menu-content">
                                                    {MEGA_MENU.map((cat, i) => {
                                                        const CatIcon = cat.icon;
                                                        return (
                                                            <div className="mega-column" key={i}>
                                                                <div className="mega-cat-header">
                                                                    <CatIcon size={20} className="cat-icon" />
                                                                    <h4>{t(cat.title)}</h4>
                                                                </div>
                                                                <ul className="mega-links">
                                                                    {cat.links.map((sub, j) => (
                                                                        <li key={j}>
                                                                            <Link to={sub.path} onClick={() => setShowMega(false)}>
                                                                                {t(sub.key)}
                                                                            </Link>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        {/* PREMIUM SEARCH BAR */}
                        <div className="search-host">
                            <div className="search-pill">
                                <Search size={18} className="icon-search" />
                                <input 
                                    type="text"
                                    placeholder={t('nav_search_placeholder')}
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onKeyDown={handleSearchSubmit}
                                    onFocus={() => {
                                        if (searchQuery) setShowResults(true);
                                        setShowMega(false);
                                    }}
                                />
                                {searchQuery && <X size={16} className="icon-clear" onClick={closeSearch} />}
                            </div>

                            {showResults && (
                                <>
                                    <div className="search-results-backdrop" onClick={closeSearch} />
                                    <div className="search-dropdown-premium">
                                        {filteredRecs.length > 0 ? (
                                            <div className="search-section">
                                                <div className="section-title">SUGGESTIONS</div>
                                                {filteredRecs.map((rec, i) => (
                                                    <div key={i} className="search-row" onClick={() => selectResult(rec.path, rec.title)}>
                                                        <div className="row-icon"><ArrowRight size={14} /></div>
                                                        <div className="row-text">
                                                            <span className="row-title">{rec.title}</span>
                                                            <span className="row-tag">{rec.category}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="not-found-state">
                                                <AlertCircle size={32} />
                                                <div className="not-found-text">
                                                    <span>Page not found</span>
                                                    <p>No matches for "{searchQuery}"</p>
                                                </div>
                                            </div>
                                        )}

                                        {history.length > 0 && (
                                            <div className="search-section history-section">
                                                <div className="section-title">RECENT SEARCHES</div>
                                                {history.map((h, i) => (
                                                    <div key={i} className="search-row history-row" onClick={() => selectResult('/contact', h)}>
                                                        <Clock size={14} />
                                                        <span className="row-title">{h}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <button className="mobile-menu-btn" onClick={toggleMenu}>
                        <div className={`ham-icon${isMenuOpen ? ' open' : ''}`}><span></span><span></span><span></span></div>
                    </button>
                </div>
            </div>

            {/* MOBILE MENU DRAWER */}
            <div className={`mobile-nav-drawer${isMenuOpen ? ' open' : ''}`}>
                <div className="drawer-header">
                    <span className="logo">BHARAT<span>INSUREX</span></span>
                    <X onClick={() => setIsMenuOpen(false)} />
                </div>
                <div className="drawer-links mobile-accordion">
                    <Link to="/" onClick={() => setIsMenuOpen(false)}>
                        <Home size={18} className="drawer-nav-icon" />
                        <span>{t('nav_home')}</span>
                    </Link>

                    {MEGA_MENU.map((cat, i) => (
                        <div key={i} className="mobile-drawer-section">
                            <div className="mobile-cat-name">
                                <cat.icon size={16} />
                                <span>{t(cat.title)}</span>
                            </div>
                            <div className="mobile-sub-links">
                                {cat.links.map((sub, j) => (
                                    <Link key={j} to={sub.path} onClick={() => setIsMenuOpen(false)}>
                                        {t(sub.key)}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}

                    <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                        <Phone size={18} className="drawer-nav-icon" />
                        <span>{t('nav_contact')}</span>
                    </Link>
                </div>
            </div>
        </header>
    );
};

export default Navbar;